import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/api/endpoints'
import { dateTime, money, qty } from '@/lib/format'
import { unitName, unitsFor } from '@/lib/units'
import QuantityStepper from '@/components/common/QuantityStepper'

/**
 * Search the whole catalog and assign any product to this line.
 *
 * Needed because the candidate list only holds what the matcher scored: a
 * line it could not read at all has an empty list, and without this there is
 * no way to correct it — only to delete it, which loses the item.
 */
function ProductPicker({ chosenId, chosenName, onChoose }) {
  const [search, setSearch] = useState('')
  const term = search.trim()

  const { data, isFetching } = useQuery({
    queryKey: ['product-search', term],
    queryFn: () => productsApi.list({ search: term, page_size: 8, is_active: true }),
    // Two characters is enough to be useful on item codes like 'MSWN'.
    enabled: term.length >= 2,
    staleTime: 60_000,
  })

  return (
    <div className="candidates">
      <div className="line-label">Or pick any product</div>

      {chosenId ? (
        <p className="muted">
          Selected: <strong>{chosenName ?? `#${chosenId}`}</strong>
        </p>
      ) : (
        <p className="missing">No product selected — this line cannot be priced.</p>
      )}

      <input
        type="search"
        value={search}
        placeholder="Search by name or item code, e.g. 2 x 12 or MSWN"
        onChange={(e) => setSearch(e.target.value)}
      />

      {term.length >= 2 && (
        <div className="candidate-list">
          {isFetching && <p className="muted">Searching…</p>}
          {!isFetching && data?.items?.length === 0 && (
            <p className="muted">Nothing matches “{term}”.</p>
          )}
          {data?.items?.map((p) => (
            <button
              type="button"
              key={p.id}
              className={`candidate${String(chosenId) === String(p.id) ? ' chosen' : ''}`}
              onClick={() => onChoose(p.id)}
            >
              <span className="candidate-name">{p.item_name}</span>
              <span className="candidate-meta">{p.item_code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * One order line in review.
 *
 * Unmatched and low-confidence lines open expanded, because those are the ones
 * that need attention; confidently matched lines stay collapsed so a reviewer
 * can scan past them.
 */
export default function OrderLineRow({
  item,
  editable,
  onSave,
  onDelete,
  onUndoWaiveMinimum,
  saving,
  // False once the order has left NEW. The matcher's doubts about a line are
  // ours, and the customer has since read every line and placed the order —
  // so the warning marks as unsafe something they have already agreed to,
  // and opens every such row on a page nobody needs to act on.
  reviewing = true,
}) {
  const needsAttention =
    reviewing && (item.match_status === 'unmatched' || item.match_status === 'suggested')
  const [open, setOpen] = useState(needsAttention)
  const [productId, setProductId] = useState(item.product_id ?? '')
  const [quantity, setQuantity] = useState(qty(item.quantity))
  const [unit, setUnit] = useState(item.unit)
  // The staff note's editor, opened from the link beside the product.
  const [noting, setNoting] = useState(false)
  // Always on. It used to be a checkbox the operator had to notice, ticked
  // by default and almost never unticked — so it asked a question whose
  // answer was already the right one. Correcting a line teaches the matcher
  // that wording, which is how it stops arriving in review at all.
  const learnAlias = true

  // Compared as numbers, so '3.0' typed over '3' is not a change.
  const quantityChanged = Number(quantity) !== Number(item.quantity)
  const dirty =
    String(productId) !== String(item.product_id ?? '') ||
    quantityChanged ||
    unit !== item.unit

  // Name for whatever is currently selected, so the picker can show the
  // choice even when it came from the candidate list above.
  const chosenProductName =
    String(productId) === String(item.product_id ?? '')
      ? item.product_name
      : item.candidates?.find((c) => String(c.product_id) === String(productId))
          ?.item_name

  const save = () => {
    const payload = { learn_alias: learnAlias }
    if (String(productId) !== String(item.product_id ?? '')) payload.product_id = Number(productId)
    if (quantityChanged) payload.quantity = Number(quantity)
    if (unit !== item.unit) payload.unit = unit
    onSave(payload)
  }

  return (
    <div className={`line-row${needsAttention ? ' attention' : ''}`}>
      <div className="line-main" onClick={() => setOpen((v) => !v)}>
        <div className="line-matched">
          {/* No 'Matched to' caption. It labelled a column that no longer has
              anything to be matched *from* — the heard text it pointed at is
              gone, and a product name needs no introduction. */}
          {item.product_name ? (
            <div className="line-product">
              {item.product_name}
              <span className="line-code">{item.item_code}</span>
              {/* On the product's own row, not a row of its own. */}
              {editable && !noting && (
                <button
                  type="button"
                  className="btn-link line-note-link"
                  onClick={(e) => {
                    e.stopPropagation()       // not a click to fold the row
                    setNoting(true)
                  }}
                >
                  {item.line_note ? 'Edit note' : '+ Add note'}
                </button>
              )}
            </div>
          ) : (
            <div className="line-product missing">No product identified</div>
          )}
          {item.line_note && !noting && (
            <div className="line-note-text">📝 {item.line_note}</div>
          )}
        </div>

        {editable ? (
          // On the bar itself, so changing a quantity is one click rather
          // than open, find the field, type, save. Clicks here stay here:
          // they must not fold the row open or shut.
          <div
            className="line-qty-edit"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && dirty && productId && !saving) save()
            }}
          >
            <QuantityStepper
              value={quantity}
              onChange={setQuantity}
              step={item.quantity_step && unit === item.unit ? Number(item.quantity_step) : undefined}
              unit={String(unit ?? '').toLowerCase()}
            />

            {/* This SKU's own units, plus whatever the line already says.
                Offering all ten let an operator put a line into a unit with
                no conversion behind it, which priced it at ₹0. */}
            <select aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
              {unitsFor(item, item.unit).map((u) => (
                <option key={u} value={u}>
                  {unitName(u)}
                </option>
              ))}
            </select>

            {/* Always laid out, shown only once something changed: a button
                that appeared from nowhere would shove this row's controls
                out of line with every row below it. */}
            <button
              type="button"
              className="btn btn-primary btn-sm line-save"
              style={{ visibility: dirty ? 'visible' : 'hidden' }}
              disabled={!dirty || saving || !productId}
              title={productId ? undefined : 'Pick a product first'}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <div className="line-qty">
            {qty(item.quantity)} {item.unit.toLowerCase()}
            {item.quantity_base && item.unit !== 'KILOGRAMS' && (
              <span className="muted"> ({qty(item.quantity_base)} kg)</span>
            )}
          </div>
        )}

        <div className="line-total">{item.line_total > 0 ? money(item.line_total) : '—'}</div>
      </div>

      {noting && (
        <LineNoteEditor
          item={item}
          saving={saving}
          onSave={(text) => {
            onSave({ line_note: text, learn_alias: false })
            setNoting(false)
          }}
          onCancel={() => setNoting(false)}
        />
      )}

      {/* Outside the fold: a line short of its minimum blocks confirming the
          order, so it is shown whether or not the row is open. */}
      {item.below_minimum && (
        <div className="line-minimum">
          <span>
            ⚠ Below the minimum order of <strong>{item.minimum}</strong>. Increase the
            quantity before confirming the order.
          </span>
        </div>
      )}
      {item.minimum_waived_by && (
        <div className="line-minimum waived">
          <span>
            Accepted below the minimum ({item.minimum}) by {item.minimum_waived_by}
            {item.minimum_waived_at && ` on ${dateTime(item.minimum_waived_at)}`}.
          </span>
          {editable && onUndoWaiveMinimum && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onUndoWaiveMinimum}>
              Undo
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="line-detail">
          {/* Both of these are the matcher showing its working, and both go
              stale the moment the customer touches the line: 0004 still
              carries 'No quantity found in this line' against a quantity the
              customer supplied themselves. Kept for the order still being
              read, dropped once it has been placed. */}
          {reviewing && item.notes && <div className="line-note">📝 {item.notes}</div>}

          {reviewing && item.candidates?.length > 0 && (
            <div className="candidates">
              <div className="line-label">Closest products in the catalog</div>
              <div className="candidate-list">
                {item.candidates.map((c) => (
                  <button
                    type="button"
                    key={c.product_id}
                    className={`candidate${String(productId) === String(c.product_id) ? ' chosen' : ''}`}
                    onClick={() => setProductId(c.product_id)}
                    disabled={!editable}
                  >
                    <span className="candidate-name">{c.item_name}</span>
                    <span className="candidate-meta">
                      {c.item_code} · {(c.score * 100).toFixed(0)}% · {c.reason}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* The candidate list is only what the matcher scored. When it found
              nothing — or found the wrong things — the reviewer still has to
              be able to reach any product in the catalog, or the line can
              only be deleted. */}
          {editable && (
            <ProductPicker
              chosenId={productId}
              chosenName={chosenProductName}
              onChoose={setProductId}
            />
          )}

          {editable && (
            // Quantity and unit live on the bar above; what is left here is
            // settling the line as a whole.
            <div className="line-edit">
              <div className="line-actions">
                {/* Not gated on `dirty`. Saving an unchanged line is the
                    operator confirming the machine read it correctly — which
                    is what clears it from review and teaches the wording.
                    Only a line with no product at all has nothing to save. */}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !productId}
                  title={
                    productId ? undefined : 'Pick a product first'
                  }
                  onClick={save}
                >
                  {saving ? 'Saving…' : dirty ? 'Save' : 'Confirm'}
                </button>
                <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The shop's own note on this product for this order — 'cut into 2 m
 * pieces'. Printed on the order sheet under the product; the customer never
 * sees it.
 */
function LineNoteEditor({ item, saving, onSave, onCancel }) {
  const [text, setText] = useState(item.line_note ?? '')
  return (
    <form
      className="line-note-edit"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(text.trim())
      }}
    >
      <input
        autoFocus
        value={text}
        maxLength={500}
        placeholder="Note for this product, e.g. cut into 2 m pieces"
        onChange={(e) => setText(e.target.value)}
      />
      <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
        Save
      </button>
      {item.line_note && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={saving}
          onClick={() => onSave('')}
        >
          Remove
        </button>
      )}
      <button type="button" className="btn btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </form>
  )
}
